import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import type { ApiClient } from '../api/client';
import ErrorBanner from '../components/ErrorBanner';
import type { ToolApproval } from '../types';
import { reportErrorLocal } from '../utils/errors';
import './TokenDetailPage.lane39.css';

type Props = {
  apiClient: ApiClient;
  setError: (message: string | null) => void;
};

type TokenDetail = {
  id: string;
  name: string;
  scopes: string[];
  app_ids: string[];
  app_scope_mode: string;
  server_scope_mode: string;
  server_ids: string[];
  default_server_id: string | null;
  operation_policy: string;
  created_at: string;
  expires_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
};

type TokenActivity = {
  id: string;
  event_kind: string;
  method?: string | null;
  path?: string | null;
  status_code?: number | null;
  detail?: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
};

function formatTimestamp(value?: string | null) {
  return value ? new Date(value).toLocaleString() : '-';
}

function statusClass(status: string) {
  const normalized = status.toLowerCase();
  if (normalized === 'applied' || normalized === 'succeeded') return 'badge badge--success';
  if (normalized === 'pending') return 'badge badge--processing';
  if (normalized === 'failed' || normalized === 'rejected' || normalized === 'denied') {
    return 'badge badge--error';
  }
  if (normalized === 'expired') return 'badge badge--warning';
  return 'badge';
}

function httpStatusClass(status?: number | null) {
  if (status == null) return 'badge badge--warning';
  if (status >= 200 && status < 300) return 'badge badge--success';
  if (status >= 400) return 'badge badge--error';
  return 'badge';
}

export default function TokenDetailPage({ apiClient, setError }: Props) {
  const { tokenId } = useParams();
  const [token, setToken] = useState<TokenDetail | null>(null);
  const [approvals, setApprovals] = useState<ToolApproval[]>([]);
  const [activity, setActivity] = useState<TokenActivity[]>([]);
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const loadTokenDetails = useCallback(async () => {
    if (!tokenId) return;
    setLoading(true);
    try {
      const [tokenRow, approvalRows, activityRows] = await Promise.all([
        apiClient.get<TokenDetail>(`/api/tokens/${tokenId}`),
        apiClient.get<ToolApproval[]>(`/api/tokens/${tokenId}/approvals?limit=100`),
        apiClient.get<TokenActivity[]>(`/api/tokens/${tokenId}/activity?limit=100`)
      ]);
      setToken(tokenRow);
      setApprovals(approvalRows);
      setActivity(activityRows);
      setLocalError(null);
    } catch (err) {
      reportErrorLocal('Failed to load token details', err, setError, setLocalError);
    } finally {
      setLoading(false);
    }
  }, [apiClient, setError, tokenId]);

  useEffect(() => {
    void loadTokenDetails();
  }, [loadTokenDetails]);

  return (
    <section className="stack token-detail token-detail39">
      <div className="card full token-detail39__intro">
        <div className="token-detail39__actions">
          <Link to="/settings/tokens">← Back to API tokens</Link>
          <button type="button" className="secondary" onClick={() => void loadTokenDetails()} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        <div className="card-header">
          <div>
            <p className="token-detail39__kicker">API token / Details</p>
            <h2>{token?.name || 'API token'}</h2>
            <p className="status">
              Inspect stored access, approval history, and recent token-authenticated API activity.
            </p>
          </div>
        </div>
        {localError && <ErrorBanner message={localError} onDismiss={() => setLocalError(null)} />}
      </div>

      {token && (
        <div className="card full token-detail39__access">
          <div className="card-header">
            <div>
              <h3>Stored access</h3>
              <p className="status">Scopes and resource limits work together. Stored scopes do not bypass permissions or approval requirements.</p>
            </div>
          </div>
          <h4 className="token-detail39__label">Lifecycle</h4>
          <dl className="token-detail39__facts">
            <div>
              <dt>Revocation</dt>
              <dd>{token.revoked_at ? 'Revoked' : 'Not revoked'}</dd>
            </div>
            <div>
              <dt>Created</dt>
              <dd>{formatTimestamp(token.created_at)}</dd>
            </div>
            <div>
              <dt>Last used</dt>
              <dd>{token.last_used_at ? formatTimestamp(token.last_used_at) : 'Not recorded'}</dd>
            </div>
            <div>
              <dt>Expires</dt>
              <dd>{token.expires_at ? formatTimestamp(token.expires_at) : 'No expiration set'}</dd>
            </div>
          </dl>
          <h4 className="token-detail39__label">Resource limits &amp; operation policy</h4>
          <dl className="token-detail39__facts">
            <div>
              <dt>App access</dt>
              <dd>{token.app_scope_mode === 'all_apps' ? 'All accessible apps' : `${token.app_ids.length} selected`}</dd>
            </div>
            <div>
              <dt>Server access</dt>
              <dd>{token.server_scope_mode === 'all_servers' ? 'All servers in scope' : `${token.server_ids.length} selected`}</dd>
            </div>
            <div>
              <dt>Default server</dt>
              <dd className="mono">{token.default_server_id || 'Not set'}</dd>
            </div>
            <div>
              <dt>Operation policy</dt>
              <dd className="mono">{token.operation_policy || 'standard'}</dd>
            </div>
          </dl>
          <h4 id="token-detail39-scopes" className="token-detail39__label">Granted scopes</h4>
          <ul className="token-scope-list" aria-labelledby="token-detail39-scopes">
            {token.scopes.map((scope) => (
              <li key={scope} className="badge">{scope}</li>
            ))}
          </ul>
          {token.scopes.length === 0 && <p className="status">No scopes granted.</p>}
        </div>
      )}

      <div className="card full">
        <div className="card-header">
          <div>
            <h3>Approval History</h3>
            <p className="status">Up to 100 recent approval requests, including resolved requests. Read-only history.</p>
          </div>
        </div>
        {approvals.length === 0 ? (
          <p className="status">No approvals recorded for this token.</p>
        ) : (
          <div className="token-event-list">
            {approvals.map((approval) => (
              <div key={approval.id} className="token-event-row">
                <div>
                  <div className="row">
                    <strong>{approval.title}</strong>
                    <span className={statusClass(approval.status)}>{approval.status}</span>
                  </div>
                  <div className="status">
                    {approval.approval_kind} · {approval.server.name} · {approval.requested_mode}
                  </div>
                  {approval.detail && <div className="status">{approval.detail}</div>}
                  {approval.cmd && <code>{approval.cmd}</code>}
                  {approval.path && <code>{approval.path}</code>}
                </div>
                <div className="status token-event-time">
                  <div>{formatTimestamp(approval.created_at)}</div>
                  {approval.resolved_at && <div>resolved {formatTimestamp(approval.resolved_at)}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card full">
        <div className="card-header">
          <div>
            <h3>Recent Activity</h3>
            <p className="status">Up to 100 recent token-authenticated API requests recorded after activity logging was enabled.</p>
          </div>
        </div>
        {activity.length === 0 ? (
          <p className="status">No activity recorded yet.</p>
        ) : (
          <div className="token-event-list">
            {activity.map((event) => (
              <div key={event.id} className="token-event-row">
                <div>
                  <div className="row">
                    <strong>{event.event_kind}</strong>
                    <span className={httpStatusClass(event.status_code)}>
                      {event.status_code ?? 'unknown'}
                    </span>
                  </div>
                  <div className="mono">
                    {[event.method, event.path].filter(Boolean).join(' ')}
                  </div>
                  {event.detail && <div className="status">{event.detail}</div>}
                </div>
                <div className="status token-event-time">{formatTimestamp(event.created_at)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
